import {
  AIActionEnvelopeSchema,
  FILTER_OPERATOR_ARITY,
  parseContract,
  type AIAction,
  type AIActionEnvelope,
  type SemanticModel,
} from '@aibi/contracts';
import {
  findIntegrityIssues,
  isCalculatedDimension,
  isCalculatedMeasure,
  isTemporalDimension,
  type SemanticModelIndex,
} from '@aibi/semantic-model';
import { AppError, err, ok, type ErrorDetail, type Result } from '@aibi/shared';

/**
 * Puerta de seguridad para respuestas de IA:
 * 1. Valida el JSON contra el contrato estricto AI Action JSON (sin SQL ni codigo).
 * 2. Verifica que cada referencia (dimensiones, medidas, jerarquias) exista en el modelo.
 * 3. Para acciones que crean objetos, comprueba que el modelo resultante siga integro.
 * Las acciones que no superan las tres fases nunca llegan al Analysis State.
 *
 * @example
 * const result = validateAIResponse(JSON.parse(llmOutput), index);
 * if (!result.ok) reportToUser(result.error.details);
 */
export function validateAIResponse(
  raw: unknown,
  index: SemanticModelIndex,
): Result<AIActionEnvelope, AppError> {
  const parsed = parseContract(AIActionEnvelopeSchema, raw, 'contrato AI Action JSON');
  if (!parsed.ok) return parsed;

  const envelope = parsed.value;
  const issues: ErrorDetail[] = [];
  if (envelope.semanticModelId !== index.model.id) {
    issues.push({
      path: 'semanticModelId',
      message: `La respuesta referencia el modelo "${envelope.semanticModelId}".`,
    });
  }
  const actionIds = new Set<string>();
  envelope.actions.forEach((action, i) => {
    if (actionIds.has(action.id)) {
      issues.push({ path: `actions.${i}.id`, message: `Id de accion duplicado "${action.id}".` });
    }
    actionIds.add(action.id);
    for (const message of findActionReferenceIssues(action, index)) {
      issues.push({ path: `actions.${i}.payload`, message });
    }
  });

  return issues.length === 0
    ? ok(envelope)
    : err(
        new AppError('VALIDATION_ERROR', 'La respuesta de IA referencia objetos inexistentes.', {
          details: issues,
        }),
      );
}

/** Problemas de una accion respecto al Semantic Model (no respecto al Analysis State). */
export function findActionReferenceIssues(action: AIAction, index: SemanticModelIndex): string[] {
  const missingDimension = (dimensionId: string): string[] =>
    index.resolveDimension(dimensionId) ? [] : [`Dimension inexistente "${dimensionId}".`];
  const missingMeasure = (measureId: string): string[] =>
    index.getMeasure(measureId) ? [] : [`Medida inexistente "${measureId}".`];
  const missingHierarchy = (hierarchyId: string): string[] =>
    index.getHierarchy(hierarchyId) ? [] : [`Jerarquia inexistente "${hierarchyId}".`];
  const requireTemporal = (dimensionId: string | undefined): string[] => {
    if (dimensionId === undefined) return ['No hay una dimension de fecha para comparar.'];
    const resolved = index.resolveDimension(dimensionId);
    if (!resolved) return [`Dimension inexistente "${dimensionId}".`];
    return isTemporalDimension(resolved) ? [] : [`"${dimensionId}" no es una dimension temporal.`];
  };

  switch (action.type) {
    case 'addDimension':
      return action.payload.timeGrain === undefined
        ? missingDimension(action.payload.dimensionId)
        : requireTemporal(action.payload.dimensionId);
    case 'removeDimension':
      return missingDimension(action.payload.dimensionId);
    case 'addMeasure':
    case 'removeMeasure':
      return missingMeasure(action.payload.measureId);
    case 'addFilter': {
      const { target, operator, values } = action.payload.filter;
      const [min, max] = FILTER_OPERATOR_ARITY[operator];
      const arity =
        values.length < min || values.length > max
          ? [`El operador "${operator}" no admite ${values.length} valores.`]
          : [];
      const reference =
        target.kind === 'dimension'
          ? missingDimension(target.dimensionId)
          : missingMeasure(target.measureId);
      return [...reference, ...arity];
    }
    case 'sort':
      return action.payload.by.flatMap(({ target }) =>
        target.kind === 'dimension'
          ? missingDimension(target.dimensionId)
          : missingMeasure(target.measureId),
      );
    case 'drillDown': {
      const { hierarchyId, levelId } = action.payload;
      const hierarchyIssues = missingHierarchy(hierarchyId);
      if (hierarchyIssues.length > 0 || levelId === undefined) return hierarchyIssues;
      return index.getHierarchyLevel(hierarchyId, levelId)
        ? []
        : [`Nivel inexistente "${levelId}".`];
    }
    case 'drillUp':
      return missingHierarchy(action.payload.hierarchyId);
    case 'comparePeriods':
      return requireTemporal(
        action.payload.comparison.dateDimensionId ?? index.model.settings.primaryDateDimensionId,
      );
    case 'createMeasure':
    case 'createDimension':
    case 'createCalculatedColumn':
      return findModelChangeIssues(index.model, applyModelAction(index.model, action));
    case 'resetSelections':
      return action.payload.dimensionId === undefined
        ? []
        : missingDimension(action.payload.dimensionId);
    case 'requestExplanation':
      return action.payload.measureId === undefined ? [] : missingMeasure(action.payload.measureId);
    case 'removeFilter':
    case 'updateFilter':
    case 'updateGrouping':
    case 'clearFilters':
    case 'setLimit':
    case 'setVisualization':
    case 'suggestVisualization':
    case 'resetAnalysis':
    case 'narrative':
      // Referencian el Analysis State, no el modelo: se validan al aplicarse.
      return [];
  }
}

type ModelAction = Extract<
  AIAction,
  { type: 'createMeasure' | 'createDimension' | 'createCalculatedColumn' }
>;

/** Modelo candidato con el objeto propuesto; el original no se modifica. */
function applyModelAction(model: SemanticModel, action: ModelAction): SemanticModel {
  switch (action.type) {
    case 'createMeasure': {
      const { measure } = action.payload;
      return isCalculatedMeasure(measure)
        ? { ...model, calculatedMeasures: [...model.calculatedMeasures, measure] }
        : { ...model, measures: [...model.measures, measure] };
    }
    case 'createDimension': {
      const { dimension } = action.payload;
      return isCalculatedDimension(dimension)
        ? { ...model, calculatedDimensions: [...model.calculatedDimensions, dimension] }
        : { ...model, dimensions: [...model.dimensions, dimension] };
    }
    case 'createCalculatedColumn':
      return { ...model, calculatedColumns: [...model.calculatedColumns, action.payload.column] };
  }
}

/** Solo las incidencias que introduce el cambio (se ignoran las preexistentes). */
function findModelChangeIssues(before: SemanticModel, after: SemanticModel): string[] {
  const key = (issue: ErrorDetail): string => `${issue.path}\u0000${issue.message}`;
  const existing = new Set(findIntegrityIssues(before).map(key));
  return findIntegrityIssues(after)
    .filter((issue) => !existing.has(key(issue)))
    .map((issue) => `${issue.path}: ${issue.message}`);
}
